/// <reference types="vite/client" />

import { initializeApp, getApps, getApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';

// Your web app's Firebase configuration
// For a Vite project, environment variables are accessed via `import.meta.env`
// Ensure you have a .env.local file in the `web/` directory with these values.

// Production Firebase configuration (fallback when the env is not set). Shared with
// web/scripts/auth-helpers.mjs, which writes the same values to
// /__/firebase/init.json for the self-hosted auth handler (PRP-MCP-11).
import prodConfig from './firebaseDefaults.json';

// Use environment variables if available, otherwise fall back to production config
const {
  VITE_FIREBASE_API_KEY = prodConfig.VITE_FIREBASE_API_KEY,
  VITE_FIREBASE_AUTH_DOMAIN = prodConfig.VITE_FIREBASE_AUTH_DOMAIN,
  VITE_FIREBASE_PROJECT_ID = prodConfig.VITE_FIREBASE_PROJECT_ID,
  VITE_FIREBASE_STORAGE_BUCKET = prodConfig.VITE_FIREBASE_STORAGE_BUCKET,
  VITE_FIREBASE_MESSAGING_SENDER_ID = prodConfig.VITE_FIREBASE_MESSAGING_SENDER_ID,
  VITE_FIREBASE_APP_ID = prodConfig.VITE_FIREBASE_APP_ID,
} = import.meta.env as ImportMetaEnv;

/**
 * Helper: mask the Firebase API key when logging so we don’t accidentally dump
 * the full secret into the console or error reporting tools.  Reveals first
 * 6 chars so you can confirm which key was loaded.
 */
const maskKey = (key: string) => (key ? `${key.slice(0, 6)}********` : '<empty>');

/**
 * In development, log the loaded Firebase environment variables so we can
 * quickly verify that Vite picked them up from the `.env` file.  This block is
 * tree-shaken out in production builds because `import.meta.env.DEV` is `false`
 * for production.
 */
if (import.meta.env.DEV) {
  // eslint-disable-next-line no-console
  console.info('[firebase] Loaded env:', {
    VITE_FIREBASE_API_KEY: maskKey(VITE_FIREBASE_API_KEY),
    VITE_FIREBASE_AUTH_DOMAIN,
    VITE_FIREBASE_PROJECT_ID,
    VITE_FIREBASE_STORAGE_BUCKET,
    VITE_FIREBASE_MESSAGING_SENDER_ID,
    VITE_FIREBASE_APP_ID: maskKey(VITE_FIREBASE_APP_ID),
  });
}

/**
 * Validate that all required Firebase environment variables are present. If any
 * are missing, throw a descriptive error so the developer knows exactly what
 * to fix instead of silently falling back to an empty string (which causes
 * hard-to-debug 400 errors from Firebase).
 */
function assertFirebaseEnv() {
  const missing: string[] = [];
  if (!VITE_FIREBASE_API_KEY) missing.push('VITE_FIREBASE_API_KEY');
  if (!VITE_FIREBASE_AUTH_DOMAIN) missing.push('VITE_FIREBASE_AUTH_DOMAIN');
  if (!VITE_FIREBASE_PROJECT_ID) missing.push('VITE_FIREBASE_PROJECT_ID');
  if (!VITE_FIREBASE_STORAGE_BUCKET) missing.push('VITE_FIREBASE_STORAGE_BUCKET');
  if (!VITE_FIREBASE_MESSAGING_SENDER_ID) missing.push('VITE_FIREBASE_MESSAGING_SENDER_ID');
  if (!VITE_FIREBASE_APP_ID) missing.push('VITE_FIREBASE_APP_ID');

  if (missing.length) {
    // Only throw an error if we're in development mode and the fallback config is not being used
    if (import.meta.env.DEV && !VITE_FIREBASE_API_KEY.startsWith('AIza')) {
      const message = `[firebase] Missing Firebase environment variables: ${missing.join(
        ', ',
      )}. Please update your /web/.env file and restart the dev server.`;
      // eslint-disable-next-line no-console
      console.error(message);
      throw new Error(message);
    }
  }
}

// Ensure env is valid before continuing.
assertFirebaseEnv();

const firebaseConfig = {
  apiKey: VITE_FIREBASE_API_KEY,
  authDomain: VITE_FIREBASE_AUTH_DOMAIN,
  projectId: VITE_FIREBASE_PROJECT_ID,
  storageBucket: VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: VITE_FIREBASE_APP_ID,
};

// --- .env.local example ---
// VITE_FIREBASE_API_KEY="AIza..."
// VITE_FIREBASE_AUTH_DOMAIN="your-project.firebaseapp.com"
// VITE_FIREBASE_PROJECT_ID="your-project-id"
// VITE_FIREBASE_STORAGE_BUCKET="your-project.appspot.com"
// VITE_FIREBASE_MESSAGING_SENDER_ID="1234567890"
// VITE_FIREBASE_APP_ID="1:12345:web:abcdef"
// --------------------------

// Initialize Firebase
// We check if the app is already initialized to prevent errors during hot-reloading in development.
const app = !getApps().length ? initializeApp(firebaseConfig) : getApp();

// Get a reference to the auth service
const auth = getAuth(app);

export { auth, app };
